package fyi.moko.hotpl.domain.model

import kotlinx.serialization.Serializable

/**
 * A place the user has actually visited and liked.
 *
 * Location ([latitude]/[longitude]) is nullable for now — it becomes meaningful
 * once the map picker arrives (Phase 3). Timestamps are epoch milliseconds and are
 * assigned by the data layer on save.
 */
@Serializable
data class Place(
    /**
     * Globally unique id, also the Firestore document id. Blank means "not persisted yet" — the
     * data layer mints one on first save. Places created before schema v9 keep their old numeric
     * row id as its decimal string ("1", "2"), because that string is already their remote
     * document id; only new places get a UUID.
     */
    val id: String = "",
    val name: String,
    val note: String? = null,
    val locationContext: String? = null,
    val tags: List<String> = emptyList(),
    val liked: Boolean = true,
    val latitude: Double? = null,
    val longitude: Double? = null,
    val createdAt: Long = 0L,
    val updatedAt: Long = 0L,
    /**
     * 대표 사진의 파일명. **[photos]가 있으면 그 중 대표로 고른 것과 같은 값이다.**
     *
     * 멀티 사진(스키마 v15) 이후에도 남겨 둔 이유는 구버전 클라이언트 때문이다 — 이 필드는
     * Firestore로 동기화되고 아직 [photos]를 모르는 앱이 이걸 읽는다. 쓰기를 멈추면 그쪽에서
     * 사진이 사라지므로, 저장할 때마다 대표 사진으로 계속 채운다.
     */
    val imageFileName: String? = null,
    /**
     * 이 장소에 붙은 사진들. 간판·내부·메뉴판처럼 시간이 지나도 그 가게의 모습인 사진이 여기
     * 남고, 그날 먹은 것과 사람은 [Visit.photos]로 간다 — 어느 쪽인지는
     * [PhotoKind.group]이 정한다.
     */
    val photos: List<PhotoRef> = emptyList(),
    val status: PlaceStatus = PlaceStatus.VISITED,
    val visibility: Visibility = Visibility.PRIVATE,
    val ownerId: String = "",
    val syncState: SyncState = SyncState.SYNCED,
    val version: Long = 1L,
    val isDeleted: Boolean = false,
    val revisitCadence: RevisitCadence? = null,
    val costFeel: CostFeel? = null,
    /** Official phone number, e.g. sourced from a Directory (Google Places) import. */
    val phoneNumber: String? = null,
    /** Official website URL, e.g. sourced from a Directory (Google Places) import. */
    val website: String? = null,
)
